import type { AppointmentPresets } from "@/types";

/**
 * Appointments rebuild 88 — readers for `user.appointment_presets`.
 *
 * The collapsed flag vocabulary deleted six per-field feature flags
 * (hide_appointment_assignee / hide_appointment_duration /
 * appointment_quick_client / appointment_category_filter /
 * calendar_travel_filter / calendar_doctor_colors) in favour of one shaped
 * payload served next to `feature_flags` on login / verify-otp / me.
 *
 * Every read goes through these helpers rather than reaching into the object,
 * because the payload is absent on an older backend and may arrive partial —
 * a missing group must fall back to the documented default, never to
 * `undefined` (which would read as "hidden" / false at a call site and quietly
 * take a field off the form).
 */
export const DEFAULT_APPOINTMENT_PRESETS: AppointmentPresets = {
  form: {
    assignee: "shown",
    duration: "shown",
    quick_client: "hidden",
    category: "hidden",
  },
  calendar: {
    travel_filter: false,
    doctor_colors: false,
    day_hours: [8, 20],
    all_travel: false,
    travel_visit_colors: false,
  },
  recalls: {
    doctor_nudges: false,
  },
};

/** What the backend may actually send: any group / key can be missing. */
export type PartialAppointmentPresets = {
  form?: Partial<AppointmentPresets["form"]>;
  calendar?: Partial<AppointmentPresets["calendar"]>;
  recalls?: Partial<AppointmentPresets["recalls"]>;
};

/** Visibility of one appointment-form field. Absent ⇒ documented default. */
export function formField(
  presets: PartialAppointmentPresets | null | undefined,
  key: keyof AppointmentPresets["form"]
): "shown" | "hidden" {
  return presets?.form?.[key] ?? DEFAULT_APPOINTMENT_PRESETS.form[key];
}

/** The calendar's boolean posture toggles (every key bar `day_hours`). */
type CalendarToggle = Exclude<keyof AppointmentPresets["calendar"], "day_hours">;

/** One calendar posture toggle. Absent ⇒ documented default (false). */
export function calendarPreset(
  presets: PartialAppointmentPresets | null | undefined,
  key: CalendarToggle
): boolean {
  return presets?.calendar?.[key] ?? DEFAULT_APPOINTMENT_PRESETS.calendar[key];
}

/**
 * The hours the calendar grids draw, [first hour, closing hour). Absent or
 * malformed (not two whole hours, closing after opening, inside 0–24) ⇒ the
 * fleet's 08:00–20:00. ArtDent 261005 sends [9, 23].
 */
export function calendarDayHours(
  presets: PartialAppointmentPresets | null | undefined
): [number, number] {
  const value = presets?.calendar?.day_hours;
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    Number.isInteger(value[0]) &&
    Number.isInteger(value[1]) &&
    value[0] >= 0 &&
    value[1] <= 24 &&
    value[0] < value[1]
  ) {
    return [value[0], value[1]];
  }
  return DEFAULT_APPOINTMENT_PRESETS.calendar.day_hours;
}
